import React from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import { IAdditionalMenuOption, MenuActions, AdditionalMenu } from '../AdditionalMenu';
import DeleteIcon from 'assets/svg/interface-16-trash.svg';
import FreezeIcon from 'assets/svg/hide-tooltip.svg';
import MenuIcon from 'assets/svg/menu-dots.svg';
import EditIcon from 'assets/svg/edit-pencil.svg';
import { ControlButton } from 'ds/components/button';
import { BulletinStatusType, IBulletin, ICommercialBulletin, PocType } from 'utils/entities';
import { NavigateToFn, Route, useRoute } from 'config/routes';
import {
  IUpdateListingStatusModal,
  UpdateListingStatusModalSource,
} from 'components/update-listing-status-popup/types';
import { EditListingStatusParams } from 'store/state/uploadBulletin/actions';


const ToggleWrapper = styled(ControlButton)<{ isRound?: boolean }>`
  ${({ theme, isRound }) => `
    padding: 0 ${isRound ? theme.spacing(1) : theme.spacing(1.5)};

    svg {
      margin: 0;
    }
  `}
`;

const IconOnlyToggleWrapper = styled.div`
  ${({ theme }) => `
    cursor: pointer;
    &&& svg {
      height: 16px;
      width: 16px;
      margin-top: ${theme.spacing(0.5)};
    }
  `}
`;
export interface ManageMenuProps {
  poi: IBulletin | ICommercialBulletin;
  iconOnly?: boolean;
  isRound?: boolean;
  dropdownPosition?: 'bottom-right' | 'botton-left';
  editBulletinRouteName: Route;
  editListingStatus: (data: EditListingStatusParams) => void;
  toggleUpdateListingStatusPopup: (v: IUpdateListingStatusModal) => void;
  navigateTo: NavigateToFn;
}

const routeToEventSourceMap: Partial<Record<Route, string>> = {
  [Route.UnitPage]: 'property_page',
  [Route.ManageBulletins]: 'manage_bulletins_page',
};

export const ManageMenu: React.FC<ManageMenuProps> = (props) => {
  const {
    poi,
    iconOnly,
    isRound,
    editBulletinRouteName,
    editListingStatus,
    toggleUpdateListingStatusPopup,
    navigateTo,
    dropdownPosition = 'bottom-left',
  } = props;
  const { t } = useLocale();
  const { route } = useRoute();

  if (!poi) return null;

  const {
    id,
    type,
    poc,
  } = poi;

  const withPrivatePromotionEventSource = route && (route.name === Route.UnitPage || route.name === Route.ManageBulletins);
  const source = withPrivatePromotionEventSource ? routeToEventSourceMap[route.name] : undefined;

  const onFreezeClick = () => {
    if (poc && poc.type === PocType.Agent) {
      editListingStatus({ status: BulletinStatusType.Frozen, id, type, source: UpdateListingStatusModalSource.ListingPage });
    }
    else {
      toggleUpdateListingStatusPopup({
        id,
        status: BulletinStatusType.Frozen,
        source: UpdateListingStatusModalSource.ListingPage,
      });
    }
  };

  const additionalMenuOptions: IAdditionalMenuOption[] = [
    {
      value: MenuActions.Freeze,
      icon: <FreezeIcon width={16} height={16} />,
      label: t('manageBulletinPage.bulletin.freeze'),
      onChange: onFreezeClick,
    },
    {
      value: MenuActions.Delete,
      icon: <DeleteIcon width={16} height={16} />,
      label: t('manageBulletinPage.bulletin.delete'),
      onChange: () => toggleUpdateListingStatusPopup({
        id,
        status: BulletinStatusType.Deleted,
        source: UpdateListingStatusModalSource.ListingPage,
      }),
    },
    {
      value: MenuActions.Edit,
      icon: <EditIcon width={16} height={16} />,
      label: t('manageBulletinPage.bulletin.editBulletin'),
      onChange: () => {
        if (editBulletinRouteName && id) {
          navigateTo(editBulletinRouteName, { id, source });
        }
      },
    },
  ];

  const styles = {
    top: '47px',
    right: dropdownPosition === 'bottom-right' ? '-165px' : '0',
    width: '204px',
  };

  const icon = <MenuIcon width={16} height={16}/>;

  let button;
  if (isRound) {
    button = (
      <ToggleWrapper size="large" isRound>
        <MenuIcon width={24} height={24} />
      </ToggleWrapper>
    );
  }
  else if (iconOnly) {
    button = <IconOnlyToggleWrapper>{icon}</IconOnlyToggleWrapper>;
  }
  else {
    button = <ToggleWrapper size="large">{icon}</ToggleWrapper>;
  }

  return (
    <AdditionalMenu selectStyles={styles} options={additionalMenuOptions}>
      {button}
    </AdditionalMenu>
  );
};
